import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { getStatusColor, CONTRACT_STATUSES } from "../../services/contractService";
import { ObligationsDashboard } from "./ObligationsDashboard";
import ExpiredContractsPopup from "../Contracts/ExpiredContractsPopup";
import { getExpiredRenewableContracts, checkAndSendReviewReminders } from "../../services/adminLoginCheckService";

const StatCard = ({ label, value, sub, color, icon, onClick }) => {
  const colorClass = color === "#c9a84c" ? "from-blue-600" :
                     color === "#8764b8" ? "from-purple-600" :
                     color === "#107c10" ? "from-green-600" :
                     color === "#0078d4" ? "from-blue-600" :
                     color === "#ca5010" ? "from-orange-600" :
                     color === "#a4262c" ? "from-red-600" :
                     "from-purple-600";
  const textColorClass = color === "#c9a84c" ? "text-blue-500" :
                         color === "#8764b8" ? "text-purple-700" :
                         color === "#107c10" ? "text-green-700" :
                         color === "#0078d4" ? "text-blue-700" :
                         color === "#ca5010" ? "text-orange-700" :
                         color === "#a4262c" ? "text-red-700" :
                         "text-purple-700";

  return (
    <div onClick={onClick} className={`bg-white border border-gray-200 rounded-xl p-6 ${onClick ? "cursor-pointer hover:border-gray-300" : "cursor-default"} transition-all relative overflow-hidden group`}>
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${colorClass} to-transparent`}/>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-gray-700 mb-2">{label}</div>
          <div className="text-4xl font-serif font-semibold text-gray-900 leading-tight">{value}</div>
          {sub && <div className={`text-sm ${textColorClass} mt-1.5`}>{sub}</div>}
        </div>
        <div className="text-2xl opacity-50">{icon}</div>
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { service, currentUser, userRole, isAdmin } = useApp();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiredContracts, setExpiredContracts] = useState([]);
  const [showExpiredPopup, setShowExpiredPopup] = useState(false);

  useEffect(() => {
    service.getListItems("Contracts").then(d => {
      const all = (d?.value?.map(i => ({...i.fields, id: i.id})) || []);
      setContracts(all);
    }).catch(() => setContracts([])).finally(() => setLoading(false));
  }, []);

  // Check for expired renewable contracts and send review reminders on admin login
  useEffect(() => {
    const checkAdminTasks = async () => {
      console.log('[Dashboard] Admin check:', {
        displayName: currentUser?.displayName,
        userRole: userRole,
        isAdmin
      });

      if (!isAdmin || !service) {
        console.log('[Dashboard] Skipping admin tasks - not admin or no service', { isAdmin, hasService: !!service });
        return;
      }

      try {
        // 1. Send review reminders to pending reviewers
        console.log('[Dashboard] Sending review reminders...');
        await checkAndSendReviewReminders(service);

        // 2. Check for expired renewable contracts
        console.log('[Dashboard] Checking for expired renewable contracts...');
        const expiredRenewable = await getExpiredRenewableContracts(service);
        console.log('[Dashboard] Expired renewable contracts result:', expiredRenewable);

        if (expiredRenewable && expiredRenewable.length > 0) {
          console.log('[Dashboard] Setting popup with', expiredRenewable.length, 'contracts');
          setExpiredContracts(expiredRenewable);
          setShowExpiredPopup(true);
        } else {
          console.log('[Dashboard] No expired renewable contracts found');
        }
      } catch (error) {
        console.error('[Dashboard] Error in admin tasks:', error);
        // Fail silently - don't break dashboard
      }
    };

    if (currentUser && service && isAdmin) {
      console.log('[Dashboard] Running admin tasks check...');
      checkAdminTasks();
    } else {
      console.log('[Dashboard] Skipping - missing currentUser/service or not admin');
    }
  }, [currentUser, service, isAdmin]);

  const regularContracts = contracts.filter(c => !c.ContractType || !c.ContractType.includes("Addendum"));
  const addenda = contracts.filter(c => c.ContractType && c.ContractType.includes("Addendum"));
  const counts = regularContracts.reduce((acc,c) => { acc[c.Status]=(acc[c.Status]||0)+1; return acc; }, {});
  const expiringSoon = regularContracts.filter(c => { if(!c.ExpiryDate) return false; const d=Math.ceil((new Date(c.ExpiryDate)-Date.now())/86400000); return d>0&&d<=30; }).length;
  const avgCycle = regularContracts.filter(c=>c.CycleDays).reduce((a,c,_,arr)=>a+(c.CycleDays/arr.length),0);
  const recent = [...regularContracts].sort((a,b)=>new Date(b.RequestDate||0)-new Date(a.RequestDate||0)).slice(0,8);

  return (
    <div>
      <ExpiredContractsPopup
        isOpen={showExpiredPopup}
        contracts={expiredContracts}
        onDismiss={() => setShowExpiredPopup(false)}
      />
      <div className="mb-7">
        <h1 className="font-serif text-3xl font-medium text-gray-900 m-0 mb-1">Dashboard</h1>
        <p className="text-gray-700 m-0">Welcome back, {currentUser?.displayName?.split(" ")[0]}. Here is your contract overview.</p>
        <div className="h-0.5 bg-gradient-to-r from-blue-600 to-transparent mt-2"/>
      </div>
      {loading ? <div className="text-center py-12 text-gray-700">Loading...</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Contracts" value={regularContracts.length} icon="📋" color="#c9a84c" sub={addenda.length + " addenda"} onClick={()=>navigate("/contracts")}/>
            <StatCard label="Addenda" value={addenda.length} icon="📎" color="#8764b8" onClick={()=>navigate("/contracts?filter=addenda")}/>
            <StatCard label="Executed" value={counts["Executed"]||0} icon="✅" color="#107c10" sub="Active" onClick={()=>navigate("/contracts?filter=executed")}/>
            <StatCard label="In Review" value={counts["In Review"]||0} icon="🔍" color="#0078d4" onClick={()=>navigate("/contracts?filter=inreview")}/>
            <StatCard label="Awaiting Execution" value={counts["Awaiting Execution"]||0} icon="⏳" color="#ca5010" onClick={()=>navigate("/contracts?filter=awaiting")}/>
            <StatCard label="Expiring (30d)" value={expiringSoon} icon="⚠️" color="#a4262c" onClick={()=>navigate("/contracts?filter=expiring")}/>
            <StatCard label="Avg Turnaround" value={avgCycle ? Math.round(avgCycle)+"d" : "--"} icon="📈" color="#8764b8" sub="End-to-end"/>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="font-serif text-xl font-medium text-gray-900">Recent Contracts</div>
                <button onClick={()=>navigate("/contracts")} className="bg-transparent border-none text-blue-600 hover:text-blue-500 cursor-pointer text-sm font-medium transition-colors">View all</button>
              </div>
              {recent.length===0 ? <p className="text-gray-700 text-sm">No contracts yet. Create your first request.</p> : recent.map((c,i)=>(
                <div key={i} onClick={()=>navigate("/contracts/"+c.id)} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-100/30 cursor-pointer mb-2 hover:bg-gray-100/50 transition-colors">
                  <div className="flex-1 overflow-hidden">
                    <div className="text-sm text-gray-900 font-medium whitespace-nowrap overflow-hidden text-ellipsis">{c.Title||"Untitled"}</div>
                    <div className="text-sm text-gray-700">{c.RefNo} · {c.VendorName}</div>
                  </div>
                  <span className="inline-block px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{c.Status}</span>
                </div>
              ))}
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="font-serif text-xl font-medium text-gray-900 mb-4">Status Breakdown</div>
              {Object.values(CONTRACT_STATUSES).map(status=>{
                const cnt=counts[status]||0;
                const pct=contracts.length?(cnt/contracts.length*100):0;
                return (
                  <div key={status} className="mb-2.5">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-700">{status}</span>
                      <span className="text-sm font-mono text-gray-700">{cnt}</span>
                    </div>
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div style={{width:pct+"%"}} className="h-full bg-gray-300 rounded-full transition-all"/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-8">
            <div className="h-0.5 bg-gradient-to-r from-blue-600 to-transparent mb-6"/>
            <ObligationsDashboard/>
          </div>
        </>
      )}
    </div>
  );
};
